import { useQuery } from "@apollo/client";
import { View } from "react-native";
import { Text, ActivityIndicator } from "react-native-paper";
import { AvatarProfile, Button, ScreenContainer, ScrollableContainer } from "../../components";
import { ScreenProps, SubmissionStatus } from "../types";
import ChipPoint from "../../components/ChipPoint";
import { readableDate } from "../../utils/dateUtils";
import { getStatusType } from "../../components/ChipPoint/index";
import { Query, QUERY } from "./types";
import { isCancelledOrExpired } from "./utils";
import { isDigital } from "../../utils/activity";

const Divider = () => <View className="border-t-2 border-sky-lighter" />;

export default function MyActivityScreen({ navigation, route }: ScreenProps<"MyActivity">) {
  const {
    params: { id },
  } = route;

  const { data } = useQuery<Query>(QUERY, {
    variables: {
      id,
    },
  });

  const submission = data?.currentMember.deferredCompSubmission;
  if (!submission) return <ActivityIndicator className="flex-1 justify-center items-center bg-sky-white" />;
  const {
    status,
    comments,
    expiresSoon,
    expirationDate,
    reviewRequestDate,
    statusChangedAt,
    participationLevel: { activity, points, requirements },
    completionDate,
  } = submission;

  const navigationToManageRequest = () => navigation.navigate("MyActivityManageRequest", { id });

  const { statusLabel, textClass, textClassDark } = getStatusType(status, expiresSoon);

  let manageButton;
  let dates;

  switch (status) {
    case SubmissionStatus.EXPIRED:
    case SubmissionStatus.CANCELLED:
      dates = (
        <Text className={`text-small-tight font-400 text-ink mt-1`}>
          {statusLabel} on {readableDate(statusChangedAt)}
        </Text>
      );
      break;
    case SubmissionStatus.APPROVED:
      dates = (
        <Text className={`text-small-tight font-400 mt-2 ${textClass}`}>
          {points} points awarded on {readableDate(statusChangedAt)}
        </Text>
      );
      break;
    default:
      manageButton = (
        <View className="sm:flex-row sm:items-center mt-6">
          <Button mode="contained" onPress={navigationToManageRequest}>
            Manage Request
          </Button>
        </View>
      );

      dates = (
        <>
          <Text className="text-small-tight font-400 text-ink mt-2">Sent on {readableDate(reviewRequestDate)}</Text>
          <Text className={`text-small-tight font-400 ${expiresSoon ? "text-red-dark" : "text-ink"} mt-1`}>
            Expires on {readableDate(expirationDate)}
          </Text>
        </>
      );
  }
  const reviewer = isDigital(activity) ? (
    <Text className="text-regular-tight font-500 text-ink-darker">Automatically awarded</Text>
  ) : (
    <View className="flex-row items-center">
      <AvatarProfile {...submission.reviewer} size={40} />
      <Text className="text-regular-tight font-500 text-ink-darker ml-3">{submission.reviewer.name}</Text>
    </View>
  );

  return (
    <ScreenContainer testID="MyActivityScreen" navigationTitle={activity.name}>
      <ScrollableContainer>
        <View className="border border-sky-lighter bg-sky-lightest sm:mt-4 sm:border-0 sm:rounded-lg">
          <View className="m-6">
            <View className="flex-row justify-between">
              <View className="w-3/4">
                <Text className={`text-regular-tight font-700 ${textClassDark}`}>{statusLabel}</Text>
                {dates}
              </View>
              {!isCancelledOrExpired(status) && (
                <View>
                  <ChipPoint {...{ points, status, expiresSoon }} />
                </View>
              )}
            </View>
            {manageButton}
          </View>
        </View>

        <View className="m-6">
          <Text className="text-small-tight font-700 text-ink-darkest pb-4">Reviewer</Text>
          {reviewer}
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 text-ink-darkest pb-4">Activity</Text>
          <Text className="text-regular-tight font-500 text-ink-darker">{activity.name}</Text>
          <Text className="text-regular-tight font-400 text-ink-dark mt-2">{activity.description}</Text>
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 text-ink-darkest pb-4">Participation Level</Text>
          <Text className="text-regular-tight font-500 text-ink-darker">{points} points</Text>
          <Text className="text-regular-tight font-400 text-ink-dark mt-2">{requirements}</Text>
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 text-ink-darkest pb-4">Activity Completion Date</Text>
          <Text className="text-regular-tight font-500 text-ink-darker">{readableDate(completionDate)}</Text>
        </View>

        <Divider />

        <View className="m-6">
          <Text className="text-small-tight font-700 text-ink-darkest pb-4">Summary of Activity</Text>
          <Text className="text-regular-tight font-500 text-ink-darker">{comments}</Text>
        </View>
      </ScrollableContainer>
    </ScreenContainer>
  );
}
